// Redux-Saga
import {
  actionChannel,
  call,
  delay,
  put,
  race,
  select,
  take,
  fork,
  cancel,
  cancelled,
  takeEvery
} from 'redux-saga/effects'

// Auth
import * as actions from '../interface/actions'
import * as actionTypes from '../interface/actionTypes'
import { DISCONNECT_REASONS } from '../constants'

// State selectors
import { getAuthConfig, getSubscriptionInfo, getConnectionInfo, getAuthScenario } from '../interface/selectors'

// Constants
import { platforms } from '../../constants'

// Errors
import BasicError, { authCodes } from '../../errors'

// Requests
import { subscribe, unsubscribe, resubscribe } from '../subscription/requests'

// Other plugins.
import { NOTIFICATION_RECEIVED } from '../../notifications/interface/actionTypes'
import * as connectivityActionTypes from '../../connectivity/interface/actionTypes'
import { connectWebsocket, disconnectWebsocket } from '../../connectivity/interface/effects'
import { getConnectionState } from '../../connectivity/interface/selectors'

// Libraries
import base64 from 'base-64'
import utf8 from 'utf8'
import { mergeValues } from '../../common/utils'

// Logging
import { logManager } from '../../logs'

// This is an Link plugin.
const platform = platforms.LINK

// Get the logger
const log = logManager.getLogger('AUTH')

/**
 * Link connect saga. This Saga is in charge of the flow for connecting and disconnecting.
 * Handles the workflow of connecting or disconnecting to SPiDR.
 * @method connectFlow
 */
export function * connectFlow () {
  while (true) {
    // Wait for a CONNECT action.
    const action = yield take(actionTypes.CONNECT)

    // Retrieve the connection info.
    const config = yield select(getAuthConfig)

    // Fork off and try to connect.
    const task = yield fork(connect, action, config)

    // Take the actions for if an error or disconnect occurs.
    const finishOrError = yield take([actionTypes.CONNECT_FINISHED, actionTypes.DISCONNECT])

    /**
     * This if block covers 2 of the 3 flows of connect / disconnect.
     * 1. The first block covers what happens when we receive a DISCONNECT action before we finish connecting.
     *        IE DISCONNECT comes before a CONNECT_FINISHED.
     * 2. The second block covers what happens when we get a successful CONNECT_FINISHED event before any DISCONNECT events.
     * 3. The third scenario is not explicitly covered here. That scenario is when we received a CONNECT_FINISHED event with an error before any disconnect events.
     *      In this case, there is nothing that we explicitly have to do, since a CONNECT_FINISHED event with an error doesn't place anything into the state.
     *      Therefore there is no real cleanup for us to do in this scenario. We then want to wait for the next CONNECT ( which we do via the next iteration of the while loop).
     **/
    if (finishOrError.type === actionTypes.DISCONNECT) {
      yield cancel(task)
    } else if (finishOrError.type === actionTypes.CONNECT_FINISHED && !finishOrError.error) {
      const disconnectAction = yield take([actionTypes.DISCONNECT, actionTypes.DISCONNECT_FINISHED])

      // if disconnect has finished, we dont need to do a teardown of auth state, or disconnect the websocket, so reset connectFlow
      if (disconnectAction.type === actionTypes.DISCONNECT_FINISHED) {
        continue
      }

      yield call(disconnect)
    }
  }
}

/**
 * Link connect function.
 * Performs the workflow of connecting to SPiDR.
 * @method connect
 */
export function * connect (action, config) {
  // Request options, to be used for all subsequent requests after connect.
  let requestOptions = {
    headers: {}
  }
  // Determine what type of connect method to use. This determines what extra
  //      information needs to be added to the request.
  const { credentials, options } = action.payload
  if (credentials) {
    const { username, password, authname, hmacToken } = credentials
    if (hmacToken) {
      // Only need the hmacToken in the x-token header when subscribing.
      // Will need to remove this once subscription succeeds.
      // Once subscription succeeds, we will receive a sessionId which we will need to use as the x-session header for subsequent requests.
      log.info('Connecting with hmacToken scenario.')
      requestOptions.headers['x-token'] = hmacToken
    } else if (authname && password) {
      log.info('Connecting with authname/password scenario.')
      requestOptions.headers.Authorization = 'Basic ' + base64.encode(utf8.encode(authname + ':' + password))
    } else if (username && password) {
      log.info('Connecting with username/password scenario.')
      requestOptions.headers.Authorization = 'Basic ' + base64.encode(utf8.encode(username + ':' + password))
    } else {
      log.info('Unexpected connect scenario.')
      const error = new BasicError({
        message: 'This login signature is not valid.',
        code: authCodes.INVALID_CREDENTIALS
      })
      yield put(actions.connectFinished({ error }, platform))
      return
    }
  }
  try {
    requestOptions = mergeValues(options, requestOptions)

    // Gather information to provide externally.
    const userInfo = {
      username: action.payload.credentials.username
    }
    // Gather information to provide internally, to other plugins.
    const connection = {
      server: {
        protocol: config.subscription.protocol,
        server: config.subscription.server,
        port: config.subscription.port,
        version: config.subscription.version
      },
      username: action.payload.credentials.username,
      // Request options that all other (Link) plugins will use.
      requestOptions
    }
    yield put(
      actions.setConnectionInfo(
        {
          userInfo,
          connection
        },
        platform
      )
    )

    const response = yield call(subscribe, config.subscription, credentials, requestOptions)

    if (response.error) {
      yield put(actions.connectFinished({ error: response.error }, platform))
      return
    }

    // If used HMAC token for authentication
    if (credentials.hmacToken) {
      // Subsequent requests must not have the x-token header and must have the x-session header.
      // Need to delete the x-token header for subsequent requests to work.
      delete requestOptions.headers['x-token']
      // Set the sessionId as the x-session header for use by subsequent requests.
      requestOptions.headers['x-session'] = response.subscriptionParams.sessionId
    }

    // Add token to connection info
    connection.token = response.token
    // Keep track of service subscription information.
    const subscription = {
      ...response.subscriptionParams,
      servicesInfo: response.servicesInfo,
      url: response.subscription
    }

    // Need to create a new object, rather than adding `url` to
    // connectionInfo in order to prevent state mutation.
    const websocketInfo = {
      ...config.websocket,
      url: subscription.notificationChannel
    }

    // Request the websocket connection.
    const wsOpenOrError = yield connectWebsocket(websocketInfo, platform)

    if (wsOpenOrError.error) {
      throw wsOpenOrError.payload
    }

    yield put(
      actions.connectFinished(
        {
          subscription,
          userInfo,
          connection
        },
        platform
      )
    )
  } catch (error) {
    // Error: login failed.
    yield put(actions.connectFinished({ error }, platform))
  } finally {
    if (yield cancelled()) {
      yield call(disconnect)
      // If we enter this point we know we got disconnect mid connection attempt BEFORE connect_finished fired.
      // Therefore we have to put any special store cleanup code in here.
      // Disconnect_finished action handles clearing up the store so no logic needs to be here right now.
    }
  }
}

/**
 * Link disconnect function.
 * Performs the actions for actually disconnecting from SPiDR.
 * @method disconnect
 */
export function * disconnect () {
  // Retrieve needed info from the store.
  const subscription = yield select(getSubscriptionInfo)
  const connection = yield select(getConnectionInfo)

  // If the above info is not present, we probably got a disconnect mid-connection attempt.
  if (subscription && connection) {
    // If the info is in the store we can go ahead and unsubscribe.
    yield call(unsubscribe, connection, subscription.url)
  }

  log.info('Successfully unsubscribed from services.')
  const wsState = yield select(getConnectionState, platform)
  if (wsState.connected) {
    log.info('Disconnecting from websocket.')
    // disconnect from the websocket
    yield disconnectWebsocket(undefined, platform)
    log.info('Successfully disconnected from websocket.')
  }

  // Dispatch disconnect finished action appropriate for the response.
  yield put(actions.disconnectFinished())
}

/**
 * Saga for extending a user's subscription.
 * When triggered, make a resub request to SPiDR for a specific subscription.
 * @method extendSubscription
 */
export function * extendSubscription () {
  // Re-subscription timer is triggered by a successful connect or a previous resub finishing.
  // Use a channel to queue the triggers (so it can catch the put resub action at the end).
  const resubTriggers = yield actionChannel([actionTypes.CONNECT_FINISHED, actionTypes.RESUBSCRIPTION_FINISHED])

  while (true) {
    const action = yield take(resubTriggers)

    // If the action was a connection error, ignore it.
    if (action.type === actionTypes.CONNECT_FINISHED && action.error) {
      continue
    }

    const isOnSSO = yield select(getAuthScenario)
    let subscription = {}
    let connection = {}

    if (isOnSSO) {
      const linkConnection = yield select(getConnectionInfo)

      connection = {
        server: linkConnection.server,
        token: action.payload.connection.token
      }
      subscription = yield select(getSubscriptionInfo)
    } else {
      connection = yield select(getConnectionInfo)
      subscription = yield select(getSubscriptionInfo)
    }

    let attemptNum
    // If the action was a failed resubscription, increment the attempt number.
    if (action.type === actionTypes.RESUBSCRIPTION_FINISHED && action.error) {
      attemptNum = action.payload.attemptNum + 1
    } else {
      attemptNum = 1
    }

    // Set the delay to be halfway between now and when the subscription expires.
    let resubDelay = (subscription.expires * 1000) / Math.pow(2, attemptNum)
    // Don't try to resub more often than every 5 minutes.
    resubDelay = resubDelay > 30000 ? resubDelay : 30000

    // Wait for either the resub delay or a disconnect action.
    const { expiry } = yield race({
      expiry: delay(resubDelay),
      disconnect: take(actionTypes.DISCONNECT_FINISHED)
    })

    // If the resubDelay expired, attempt resubscription
    if (expiry) {
      const response = yield call(resubscribe, connection, subscription, isOnSSO)

      if (response.error) {
        yield put(
          actions.resubscribeFinished(
            {
              error: response.error,
              attemptNum
            },
            platform
          )
        )
      } else {
        // TODO: Check response.serviceInfo for full/partial resubscription.
        yield put(actions.resubscribeFinished({ attemptNum }, platform))
      }
    }
  }
}

/**
 * Saga for updating a subscription's services.
 * @method updateSubscription
 */
export function * updateSubscription () {
  while (true) {
    const action = yield take(actionTypes.UPDATE_SUBSCRIPTION)
    log.debug("Attempting to update subscription's services.", action.payload)

    const isOnSSO = yield select(getAuthScenario)
    const connection = yield select(getConnectionInfo)
    const subscription = yield select(getSubscriptionInfo)
    subscription.service = action.payload

    // Use the resubscribe request to update the subscription. The endpoint is for both.
    const response = yield call(resubscribe, connection, subscription, isOnSSO)

    if (response.error) {
      log.debug('Failed to update subscription services.')
      yield put(
        actions.updateSubscriptionFinished(
          {
            error: new BasicError({
              message: response.message,
              code: authCodes.LINK_UPDATE_SUBSCRIPTION_FAIL
            })
          },
          platform
        )
      )
    } else {
      log.debug('Successfully updated subscription services.', action.payload)
      // TODO: Use response.serviceInfo to update full/partial subscription.
      // Only update the service substate in subscription.
      yield put(
        actions.updateSubscriptionFinished(
          {
            subscription: {
              service: action.payload
            }
          },
          platform
        )
      )
    }
  }
}

/**
 * Saga that handles a "subscription gone" notification.
 * Treat the notification as a forced disconnect.
 * @method onSubscriptionGone
 */
export function * onSubscriptionGone () {
  // Redux-saga take() pattern.
  // Take notifications about the subscription being 'gone'.
  function takeGoneNotification (action) {
    return (
      action.type === NOTIFICATION_RECEIVED &&
      action.payload.notificationMessage &&
      action.payload.notificationMessage.eventType === 'gone'
    )
  }

  // Take "resubscription failed" actions caused by the subscription not existing.
  function takeResubGoneSubscription (action) {
    return (
      action.type === actionTypes.RESUBSCRIPTION_FINISHED &&
      action.error &&
      // Magic number: 39 is KL's statusCode for "resource does not exist".
      action.payload.code === 39
    )
  }

  while (true) {
    yield take([takeGoneNotification, takeResubGoneSubscription])

    // Dispatch an action to disconnect the websocket (and let the connectivity
    //      plugin know we expect it to be disconnected).
    const wsState = yield select(getConnectionState, platform)
    if (wsState.connected) {
      yield disconnectWebsocket(undefined, platform)
    }

    // Dispatch a disconnect finished action to trigger "user disconnected" logic.
    yield put(actions.disconnectFinished({ reason: DISCONNECT_REASONS.GONE }))
  }
}

/**
 * Triggers onConnectionLost saga when a connectivity.WS_RECONNECT_FAILED actionType occurs
 * @method onConnectionLostEntry
 */
export function * onConnectionLostEntry () {
  yield takeEvery(connectivityActionTypes.WS_RECONNECT_FAILED, onConnectionLost)
}

/**
 * Handles lost connections from the connectivity plugin
 * @method onConnectionLost
 */
export function * onConnectionLost () {
  yield put(actions.disconnectFinished({ reason: DISCONNECT_REASONS.LOST_CONNECTION }))
}
